/**
 * The public API, and nothing else.
 *
 * Every call this file makes is under `/api/public/`. The admin routes exist on
 * the same server and are deliberately not modelled here — a guest bundle that
 * knows how to publish a tariff version is a guest bundle someone will
 * eventually point at a park's API from a browser console.
 *
 * The three rules `docs/decisions.md` §7 states are the API's, not ours, but
 * two of them shape this client. A price we send is never believed, so
 * `expected` is a report of what we were showing and never an instruction; and
 * `total` must never be read without `status`, because the route returns
 * `total: 0` alongside `status: 'unavailable'` and a widget that reads only the
 * number advertises a free stay. `quoteOf` below is the single place that
 * pairing is enforced.
 */

export interface Countries {
  /** What the form opens on. Null when the park has not said. */
  default: string | null;
  /** Ordering hint for the picker, never a restriction. */
  nearby: readonly string[];
  all: readonly string[];
}

export type AttributeKind = 'number' | 'boolean' | 'enum' | 'text';

export interface AttributeDef {
  id: string;
  label: string;
  kind: AttributeKind;
  options?: string[];
  unit?: string;
  filterable?: boolean;
  sort?: number;
}

export type AttributeValue = string | number | boolean;

/**
 * One of the guest categories a card prices by.
 *
 * `key` is the card's own name for it — `volwassene`, `q0`, whatever the
 * operator typed — and is what a quote's `quantities` map must be keyed by.
 * `channel` is the stable role that key plays: adult, child, infant, other.
 *
 * The distinction is the whole point. A park counts guests however its price
 * list needs; every OTA counts them exactly one way. So the picker speaks roles
 * and the wire speaks the card's keys, and this field is the translation.
 */
export interface QuantityDef {
  key: string;
  label?: string;
  /** The channel role. Absent means the key is already a role. */
  channel?: string;
  occupies?: boolean;
  includedInBase?: number;
}

export interface Accommodation {
  id: string;
  label: string;
  kind: string;
  attributes: Record<string, AttributeValue>;
  /** How many units the type has. Not how many are free. */
  units: number;
  /**
   * What this card prices by, if it prices by anybody.
   *
   * Absent or empty means the card prices per unit rather than per person, and
   * a party sent for it is ignored rather than refused.
   */
  quantities?: QuantityDef[];
}

/**
 * What the deployment we are talking to can actually answer.
 *
 * Advertised by the server rather than configured here, so a price-calculator
 * deployment and a full PMS are served by one build with nothing to keep in
 * step. A flag set in two places is a flag that ends up disagreeing with itself.
 */
export interface Features {
  /**
   * Whether a guest may complete a booking, or only enquire.
   *
   * False for the 2027 price calculator, which prices stays and tracks what is
   * free — reception blocks units by hand — but has no reservations, so nothing
   * can allocate a unit without a person.
   *
   * Availability is deliberately NOT a feature: both products have it, and a
   * price calculator that ignored a closure entered at reception would offer a
   * pitch somebody had shut.
   *
   * Absent is read as TRUE. A server too old to say is the full product, and
   * guessing the other way would quietly stop a working site selling.
   */
  bookings: boolean;
}

export interface Catalogue {
  countries: Countries;
  attributes: AttributeDef[];
  accommodations: Accommodation[];
  features: Features;
}


/**
 * A window of availability, as the park publishes it.
 *
 * `periods` is null for a type the park enumerates no units of — a camping
 * field sold by the pitch, never numbered. That is "ask us", NOT "free all
 * season": an empty period set would claim availability the park has not
 * asserted, and in a combined view one such type would whitewash every other.
 */
export interface CalendarWindow {
  /** The range this answer covers. Outside it, nothing is known. */
  from: string;
  until: string;
  /** The longest stay the API will price. */
  maxNights: number;
  accommodations: Array<{
    id: string;
    periods: { closed: IsoWindow[]; switching: IsoWindow[] } | null;
    rules: IsoRule[];
  }>;
}

export interface IsoWindow { from: string; until: string }

export interface IsoRule {
  from: string;
  until: string;
  /**
   * What the rule does. `block` cannot be had; `contact` and `indicative` can.
   *
   * Optional for a server that predates the field, and defaulted to `block`
   * there — the safe direction for a caller that cannot tell, since offering a
   * date the park cannot sell is worse than withholding one it can.
   */
  effect?: 'block' | 'contact' | 'indicative';
  arriveOn?: number[];
  departOn?: number[];
  minNights?: number;
  maxNights?: number;
}

export interface AvailabilityDay {
  from: string;
  nights: number;
  // `free: null` means the park enumerates no units for this type — camping,
  // usually — which is NOT the same as none being free. Zero would read as sold
  // out; null reads as "ask us".
  accommodations: Array<{ id: string; free: number | null }>;
}

/** How a stay may be had, if at all. */
export type Booking = 'direct' | 'contact' | 'indicative';

export interface Quote {
  accommodationId: string;
  arrival: string;
  nights: number;
  status: string;
  /**
   * Null where the park quotes this stay by hand.
   *
   * Withheld by the API rather than hidden here: a stay under a "contact us"
   * rule is one the park will not publish a figure for, so there is no figure
   * to publish. `priceOf` turns it into the same null as an unavailable stay,
   * and every tile already knows what to draw for that — price on request,
   * with a way to ask.
   */
  total: number | null;
  extras: Array<{ label?: string; id?: string; total?: number; optional?: boolean }>;
  extrasTotal: number;
  /** Absent where the park enumerates no units of this type. See `priceOf`. */
  available?: boolean;
  /**
   * The units of this type a guest may ask for BY NAME, for THESE dates.
   *
   * Empty for almost every type, and that is the default: units under a type
   * are interchangeable and the park assigns one. Non-empty where the operator
   * has said these units are worth choosing between — a park renting three
   * caravans as one product at one price.
   */
  units?: Array<{ id: string; label: string; free: boolean }>;
  booking: Booking;
  restriction?: { reason?: string; label?: string };
  /** Set when our `expected` disagreed with what the API worked out. */
  priceChanged?: boolean;
}

export interface InquiryInput {
  accommodationId: string;
  arrival: string;
  nights: number;
  quantities: Record<string, number>;
  /**
   * The same people in the picker's own roles — adults, children, infants,
   * pets. `quantities` is those people counted in the card's categories, which
   * is what prices the stay and which may fold two roles into one number.
   * Reception reads this one, so it is sent as well as, not instead of.
   */
  party?: Record<string, number>;
  /**
   * What the guest was looking at when they pressed the button.
   *
   * Sent so a disagreement is RECORDED. It is not the price of the enquiry —
   * the API recomputes that and stores its own number in `price`, which is the
   * flaw in the legacy calculator being fixed. Never send a figure this browser
   * worked out itself; only one the API has already given us.
   */
  quotedPrice?: number;
  name: string;
  email: string;
  country: string;
  phone?: string;
  street?: string;
  houseNumber?: string;
  houseNumberAddition?: string;
  postalCode?: string;
  city?: string;
  licencePlate?: string;
  comment?: string;
  locale?: string;
  /**
   * Proof the form was opened before it was submitted.
   *
   * Fetched from the API when the form appears. Friction against scripts that
   * POST straight at the endpoint; not a security control, and the API only
   * requires it when a park has asked it to.
   */
  formToken?: string;
  /** How long the guest spent on the form. A fill of under two seconds was not typed. */
  elapsedMs?: number;
  /**
   * The honeypot: a field no person can see, so anything in it came from a script
   * that fills every input by name. Always sent, always empty.
   */
  website?: string;
}

/**
 * What the API says when an enquiry is accepted.
 *
 * `token` is the key to the enquiry's own page. It is returned rather than a
 * built URL because this browser is already on the site and can build its own
 * link — and must be able to whether or not the park has configured the
 * absolute site URL that the MAIL needs.
 *
 * It is a secret. It belongs in the address bar and nowhere else: not in a log,
 * not in an analytics call, not in anything that would carry a URL off this
 * origin.
 *
 * Still no price. Echoing the recomputed figure back would invite a client to
 * compare and retry until it liked the answer.
 */
export interface InquiryReceipt {
  id: string;
  received: boolean;
  token: string;
}

/** An enquiry as its own page shows it: what was asked for, and by whom. */
export interface InquiryDetail {
  id: string;
  accommodationId: string;
  accommodation: string;
  arrival: string;
  departure: string;
  nights: number;
  party: Record<string, number>;
  /** The same people in channel roles. Null on rows taken before it was
      stored, where `party` and `partyLabels` are all there is. */
  guestParty?: Record<string, number> | null;
  /** The unit they asked for by name, where they asked for one. */
  unit?: string | null;
  /** The card's own word for each party key, where the operator wrote one. */
  partyLabels: Record<string, string>;
  price: number | null;
  /**
   * Why there is none, where the park declined to give one.
   *
   * The guest saw this sentence when they searched. The page they land on
   * should not suddenly go quiet about it — and must never show a figure the
   * park withheld.
   */
  priceWithheldBecause: string | null;
  currency: string;
  name: string;
  email: string;
  phone: string | null;
  street: string | null;
  houseNumber: string | null;
  houseNumberAddition: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  licencePlate: string | null;
  comment: string | null;
  createdAt: string;
  /**
   * How soon the park says it usually answers, or null.
   *
   * Null means the park has made no promise, and the page then says nothing
   * about timing rather than inventing something. Already resolved to a unit
   * and a count by the API, from the same rule the mail uses — see
   * `packages/shared/src/replyPromise.ts`.
   */
  replyWithin: { unit: 'hours' | 'days'; count: number } | null;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public request: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiOptions {
  apiBase: string;
  parkId?: string;
  /** Injected in tests and by the plugin, which may want its own retry policy. */
  fetch?: typeof fetch;
  signal?: AbortSignal;
}

export class PublicApi {
  private readonly base: string;

  private readonly headers: Record<string, string>;

  private readonly doFetch: typeof fetch;

  constructor(opts: ApiOptions) {
    this.base = opts.apiBase.replace(/\/+$/, '');
    this.headers = { 'content-type': 'application/json' };
    // The park scope. One park today, so it is normally absent and the API's
    // own default answers — reading it from configuration means multi-park is a
    // deployment change rather than a code change.
    if (opts.parkId) this.headers['x-park'] = opts.parkId;
    this.doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => globalThis.fetch(...a));
  }

  private async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    const res = await this.doFetch(`${this.base}${path}`, {
      headers: this.headers,
      signal,
    });
    if (!res.ok) throw new ApiError(res.status, await safeMessage(res), `GET ${path}`);
    return (await res.json()) as T;
  }

  private async post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    const res = await this.doFetch(`${this.base}${path}`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok) throw new ApiError(res.status, await safeMessage(res), `POST ${path}`);
    return (await res.json()) as T;
  }

  async catalogue(signal?: AbortSignal): Promise<Catalogue> {
    const body = await this.get<Catalogue>('/accommodations', signal);
    // Defaulted here, once, rather than at every use. A server too old to
    // declare itself is a full PMS — guessing the other way would make a
    // working site quietly stop reporting availability after an upgrade.
    return {
      ...body,
      features: { bookings: body.features?.bookings !== false },
    };
  }

  /**
   * A whole window's availability, in one request.
   *
   * The other route answers one arrival and one length at a time, so a month
   * costs up to 42 calls and can only be afforded once a length is known. This
   * returns the compressed periods the park computed once, and every question a
   * calendar asks is then answered locally against them.
   */
  calendar(from: string, until: string, signal?: AbortSignal): Promise<CalendarWindow> {
    return this.get<CalendarWindow>(
      `/calendar?from=${encodeURIComponent(from)}&until=${encodeURIComponent(until)}`, signal,
    );
  }

  availability(from: string, nights: number, signal?: AbortSignal): Promise<AvailabilityDay> {
    return this.get<AvailabilityDay>(
      `/availability?from=${encodeURIComponent(from)}&nights=${nights}`, signal,
    );
  }

  quote(body: {
    accommodationId: string; arrival: string; nights: number;
    quantities: Record<string, number>; expected?: number;
  }, signal?: AbortSignal): Promise<Quote> {
    return this.post<Quote>('/quote', body, signal);
  }

  inquiry(body: InquiryInput, signal?: AbortSignal): Promise<InquiryReceipt> {
    return this.post('/inquiries', body, signal);
  }

  /**
   * One enquiry back, for the page a guest lands on after sending it.
   *
   * The token is the key, not the reference — the reference is short, quotable
   * and printed in mail subjects, and this response is somebody's home
   * address. See the endpoint for the whole argument.
   */
  inquiryDetail(id: string, token: string, signal?: AbortSignal): Promise<InquiryDetail> {
    return this.get<InquiryDetail>(
      `/inquiries/${encodeURIComponent(id)}?t=${encodeURIComponent(token)}`, signal,
    );
  }

  /** A token for a form about to be filled in. See `InquiryInput.formToken`. */
  formToken(signal?: AbortSignal): Promise<{ token: string; minAgeMs: number }> {
    return this.get('/form-token', signal);
  }
}

/**
 * The one place `total` and `status` are read together.
 *
 * §7 lists it as known and unfixed: `/api/public/quote` returns `total: 0`
 * alongside `status: 'unavailable'`. Rather than trusting every call site to
 * remember, a quote is turned into a price-or-nothing here and the rest of the
 * app has no way to ask for the number without the verdict.
 */
/**
 * The price to show, or null when there is nothing honest to show.
 *
 * `available` has THREE states, and this used to read it as two.
 *
 *   true      the park counts units of this type and one is free
 *   false     it counts them and none is
 *   ABSENT    it counts no units of this type at all
 *
 * The third is not a gap. A camping field is sold by the pitch and the park has
 * never numbered its pitches, so "how many are free" is a question with no
 * answer rather than an answer of zero — which is why the API omits the field
 * instead of sending `false`.
 *
 * `!q.available` collapsed absent into false, so every accommodation the park
 * does not enumerate showed no price at all. The stay is priced, the price is
 * right, and the tile said nothing. Availability being unknown is a separate
 * claim from the price being unknown, and only one of them was true.
 */
export function priceOf(q: Quote): number | null {
  if (q.status === 'unavailable' || q.available === false) return null;
  // Null where the park withheld it. See `Quote.total`.
  return q.total ?? null;
}

async function safeMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: unknown };
    return typeof body.error === 'string' ? body.error : res.statusText;
  } catch {
    return res.statusText;
  }
}
